import type { Painter } from '../draw/painter';
import type { Terrain } from '../content/types';
import { CHUNK, type Chunk, type WorldGen } from './generator';
import { TERRACE } from './relief';

/**
 * (track 2, owner Sep 11: "the pixelated shore lines, and the other places where a colour changes") The ground's edges.
 *
 * The ground is one flat rectangle per tile and a tile's terrain is one class decided at its centre, so every boundary was
 * a one-metre staircase — worst on a shore, then wherever two ground colours meet, and again on the dark terrace step
 * lines. This paints the same tiles with curved edges, and changes nothing else: not the fields, not the chunk's terrain,
 * not what a tile *is* for walking, driving or building. Paint only, once per chunk when its ground cache is drawn.
 *
 * The one idea, three times over — marching squares on a field whose *sign* is the tile's class:
 *
 * * **The shore.** Each tile's value is ±1 by its class (water negative), with a magnitude from how far its elevation is
 *   from sea level, so the curve bends the way the ground does. Corner values are the mean of the four tiles that meet
 *   there; the shore is the zero contour through each boundary tile, one polygon. A second contour a little further out
 *   is the shallows band. Crossings are clamped to the middle of a tile edge so no tile ever changes side entirely: the
 *   painted shore is never more than a tile from the physical one.
 * * **Land against land.** The same on a blurred ±1 class field for each pair of neighbouring classes: a staircase
 *   becomes a curve within a tile of it; a lone speckle tile vanishes into its neighbours and a cluster becomes a patch.
 * * **The terrace steps.** The dark step line is the contour of the terraced height, continuous by construction, instead
 *   of a dark edge on each tile.
 *
 * And the water's two colours blend by a smooth field instead of a per-tile coin, so open water is not a checkerboard.
 * Neighbours outside the chunk come from the live chunk next door when the caller has it, else from the field — the
 * same classes bar a road's pavement, so a seam can differ by a polygon's bend and no more.
 */

const PAD = 2, SPAN = CHUNK + 2 * PAD;
/** a contour crossing stays this far from a tile's corner (fraction of the edge): the guarantee that no tile flips side */
export const CLAMP = 0.2;
/** elevation units from sea level at which a tile's shore value reads ±1 — the width of the bend the shore can take */
const SHORE_BAND = 0.007;
/** the shallows: the contour of the shore field at this value, inside the water */
const SHALLOW_AT = -0.55;
const SHALLOW_TINT = '#9fd0e0', SHALLOW_MIX = 0.4;

const groundCache = new Map<string, string>();
/** a terrain colour under the relief: `lit` −64..64 (0 = flat), height in metres — cached, since a chunk asks 4096 times */
export function groundColour(base: string, lit: number, height: number): string {
  const l = Math.round(lit / 8), a = Math.min(8, Math.max(0, Math.round(height / 20)));
  const key = `${base}|${l}|${a}`;
  let out = groundCache.get(key);
  if (out) return out;
  const n = parseInt(base.slice(1), 16); let r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const litK = Math.max(0.35, Math.min(1.5, 1 + (l * 8) / 64)), alt = a / 8;
  r = r * litK * (1 - alt * 0.35) + alt * 120; g = g * litK * (1 - alt * 0.3) + alt * 116; b = b * litK * (1 - alt * 0.2) + alt * 110;
  out = `#${((1 << 24) | (Math.min(255, Math.round(r)) << 16) | (Math.min(255, Math.round(g)) << 8) | Math.min(255, Math.round(b))).toString(16).slice(1)}`;
  if (groundCache.size > 20000) groundCache.clear();
  groundCache.set(key, out);
  return out;
}

const mixCache = new Map<string, string>();
function mix(a: string, b: string, k: number): string {
  const key = `${a}${b}${k}`;
  let out = mixCache.get(key); if (out) return out;
  const pa = parseInt(a.slice(1), 16), pb = parseInt(b.slice(1), 16);
  const ch = (s: number) => Math.round(((pa >> s) & 255) * (1 - k) + ((pb >> s) & 255) * k);
  out = `#${((1 << 24) | (ch(16) << 16) | (ch(8) << 8) | ch(0)).toString(16).slice(1)}`;
  if (mixCache.size > 4000) mixCache.clear();
  mixCache.set(key, out);
  return out;
}

/**
 * The part of the unit cell where the field is below `thr`, as a polygon: corners below, and the crossing on every edge
 * whose ends disagree. Crossings clamped to [CLAMP, 1 − CLAMP] along the edge. Corners are v[0] top-left, v[1] top-right,
 * v[2] bottom-right, v[3] bottom-left. Returns the points offset by (ox, oy); an empty array when nothing is below.
 */
export function belowPoly(v: ArrayLike<number>, thr: number, ox = 0, oy = 0): [number, number][] {
  const pts: [number, number][] = [];
  // the saddle — two opposite corners below, the other two above — is two separate triangles unless the cell's middle is
  // below too; walked in order it would come out as one bow-tie band. Split it by its middle.
  const b0 = v[0] < thr, b1 = v[1] < thr, b2 = v[2] < thr, b3 = v[3] < thr;
  if (b0 === b2 && b1 === b3 && b0 !== b1 && (v[0] + v[1] + v[2] + v[3]) / 4 >= thr) {
    // two triangles, returned as one polygon that pinches at the middle: corner, crossing, centre, crossing, corner, crossing, centre, crossing
    const cx = ox + 0.5, cy = oy + 0.5, out: [number, number][] = [];
    for (let i = 0; i < 4; i++) {
      if (!(v[i] < thr)) continue;
      const ax = i === 1 || i === 2 ? 1 : 0, ay = i >= 2 ? 1 : 0;
      const prev = v[(i + 3) & 3], next = v[(i + 1) & 3];
      let tp = (thr - v[i]) / (prev - v[i]); tp = tp < CLAMP ? CLAMP : tp > 1 - CLAMP ? 1 - CLAMP : tp;
      let tn = (thr - v[i]) / (next - v[i]); tn = tn < CLAMP ? CLAMP : tn > 1 - CLAMP ? 1 - CLAMP : tn;
      const pi = (i + 3) & 3, ni = (i + 1) & 3; // the previous and the next corner, in the same 0 → 3 order
      const px = pi === 1 || pi === 2 ? 1 : 0, py = pi >= 2 ? 1 : 0, nx = ni === 1 || ni === 2 ? 1 : 0, ny = ni >= 2 ? 1 : 0;
      out.push([ox + ax + (px - ax) * tp, oy + ay + (py - ay) * tp], [ox + ax, oy + ay], [ox + ax + (nx - ax) * tn, oy + ay + (ny - ay) * tn], [cx, cy]);
    }
    return out;
  }
  for (let i = 0; i < 4; i++) {
    const a = v[i], b = v[(i + 1) & 3];
    const ax = i === 1 || i === 2 ? 1 : 0, ay = i >= 2 ? 1 : 0, bx = i === 0 || i === 1 ? 1 : 0, by = i === 1 || i === 2 ? 1 : 0;
    if (a < thr) pts.push([ox + ax, oy + ay]);
    if ((a < thr) !== (b < thr)) { let t = (thr - a) / (b - a); t = t < CLAMP ? CLAMP : t > 1 - CLAMP ? 1 - CLAMP : t; pts.push([ox + ax + (bx - ax) * t, oy + ay + (by - ay) * t]); }
  }
  return pts;
}

/** only the crossings of the contour with the cell's edges — the line the contour draws through the cell */
function crossings(v: ArrayLike<number>, thr: number, ox: number, oy: number): [number, number][] {
  const pts: [number, number][] = [];
  for (let i = 0; i < 4; i++) {
    const a = v[i], b = v[(i + 1) & 3];
    if ((a < thr) === (b < thr)) continue;
    const ax = i === 1 || i === 2 ? 1 : 0, ay = i >= 2 ? 1 : 0, bx = i === 0 || i === 1 ? 1 : 0, by = i === 1 || i === 2 ? 1 : 0;
    let t = (thr - a) / (b - a); t = t < CLAMP ? CLAMP : t > 1 - CLAMP ? 1 - CLAMP : t;
    pts.push([ox + ax + (bx - ax) * t, oy + ay + (by - ay) * t]);
  }
  return pts;
}

// the padded grids, reused across paints (a paint is synchronous)
const tid = new Int16Array(SPAN * SPAN), water = new Uint8Array(SPAN * SPAN), level = new Int16Array(SPAN * SPAN), shoreV = new Float32Array(SPAN * SPAN);
const colour: string[] = new Array(SPAN * SPAN);
const v4 = new Float64Array(4), s25 = new Int8Array(25);
const SIDES: readonly (readonly [number, number])[] = [[-1, 0], [1, 0], [0, -1], [0, 1]];
const indexByGen = new WeakMap<WorldGen, Map<string, number>>();

/** the water's colour: its two colours blended by a smooth field, quantised so the strings cache */
function calmWater(t: Terrain, k: number): string {
  if (!t.color2) return t.color;
  return mix(t.color, t.color2, Math.round((0.5 + 0.5 * Math.max(-1, Math.min(1, k * 3))) * 12) / 12);
}

/**
 * Paint a chunk's terrain — every tile's ground colour under the relief, the curved edges, the terrace contour lines — with
 * the painter already `begin`-ed at the chunk's origin. `neighbour` returns a generated chunk next door when the caller
 * has one (the game's live chunks), so the edges agree across the seam.
 */
/**
 * (owner, Sep 13: "dirt, grass, concrete look pixelated") the two colours of a terrain used to be picked per tile by a coin
 * toss — a one-tile checker that reads as pixels at any zoom. Now a soft field — value noise on a three-tile lattice, smoothly
 * interpolated — blends them, in five steps so a row still paints as a few runs rather than sixty-four rects: blotches a few
 * tiles wide that fade into each other, the way the ground edges already do.
 */
const MOTTLE_CELL = 3, MOTTLE_STEPS = 4;
const mottleCache = new Map<string, string>();
function mottle(f: { speckle(x: number, y: number): number }, a: string, b: string, x: number, y: number): string {
  const u = x / MOTTLE_CELL, v = y / MOTTLE_CELL, i = Math.floor(u), j = Math.floor(v);
  const s = (t: number) => t * t * (3 - 2 * t), fu = s(u - i), fv = s(v - j);
  const n = (f.speckle(i, j) * (1 - fu) + f.speckle(i + 1, j) * fu) * (1 - fv) + (f.speckle(i, j + 1) * (1 - fu) + f.speckle(i + 1, j + 1) * fu) * fv;
  const q = Math.round(n * MOTTLE_STEPS) / MOTTLE_STEPS;
  const key = `${a}${b}${q}`;
  let c = mottleCache.get(key);
  if (!c) { c = mix(a, b, q); mottleCache.set(key, c); }
  return c;
}
export function paintTerrain(p: Painter, gen: WorldGen, c: Chunk, neighbour?: (cx: number, cy: number) => Chunk | undefined): void { const it = paintTerrainSteps(p, gen, c, neighbour); while (!it.next().done); }
/**
 * (owner, Sep 25: the jerking at speed) the same as a generator that stops every `rows` rows, so a caller with a frame's spare time
 * can paint a chunk a slice at a time (src/engine/world-stream.ts `prepaint`). The grids below are module-wide scratch: one chunk's
 * paint in progress at a time, and nothing else may paint terrain until it has run to the end.
 */
export function* paintTerrainSteps(p: Painter, gen: WorldGen, c: Chunk, neighbour?: (cx: number, cy: number) => Chunk | undefined, rows = 8): Generator<void, void, void> {
  const f = gen.fields, list = gen.terrainList;
  let index = indexByGen.get(gen);
  if (!index) { index = new Map(list.map((t, i) => [t.id, i])); indexByGen.set(gen, index); }
  const sea = f.seaLevel;
  // 1. the grids: terrain, water, terrace level and the painted colour of every tile, plus a two-tile apron
  for (let gy = 0; gy < SPAN; gy++) { if (gy && gy % (rows * 2) === 0) yield; for (let gx = 0; gx < SPAN; gx++) {
    const g = gy * SPAN + gx, x = c.x0 + gx - PAD, y = c.y0 + gy - PAD;
    const lx = gx - PAD, ly = gy - PAD;
    let ti: number, lit: number, h: number;
    if (lx >= 0 && ly >= 0 && lx < CHUNK && ly < CHUNK) { const i = ly * CHUNK + lx; ti = c.terrain[i]; lit = c.lit[i]; h = c.height[i]; }
    else {
      const nb = neighbour?.(Math.floor(x / CHUNK), Math.floor(y / CHUNK));
      if (nb) { const i = (y - nb.y0) * CHUNK + (x - nb.x0); ti = nb.terrain[i]; lit = nb.lit[i]; h = nb.height[i]; }
      else { ti = index.get(f.terrainAt(x, y).id) ?? 0; lit = 0; h = c.height[Math.min(CHUNK - 1, Math.max(0, ly)) * CHUNK + Math.min(CHUNK - 1, Math.max(0, lx))]; }
    }
    const t = list[ti], w = !t.walkable && !t.buildable;
    tid[g] = ti; water[g] = w ? 1 : 0; level[g] = Math.round(h / TERRACE); shoreV[g] = Number.NaN;
    if (w) colour[g] = calmWater(t, f.flavor(x & ~1, y & ~1));
    else { const base = t.color2 ? mottle(f, t.color, t.color2, x, y) : t.color; colour[g] = groundColour(base, lit, h); }
  } }
  yield;
  // the shore field, lazily: ±1 by class, the magnitude from the elevation's distance to sea level
  const shore = (g: number): number => {
    let s = shoreV[g];
    if (Number.isNaN(s)) { const x = c.x0 + (g % SPAN) - PAD, y = c.y0 + Math.floor(g / SPAN) - PAD; const d = Math.min(1, Math.abs(f.elevation(x, y) - sea) / SHORE_BAND); s = (water[g] ? -1 : 1) * (0.5 + 0.5 * d); shoreV[g] = s; }
    return s;
  };
  // the corner values of a per-tile field around tile g: the mean of the four tiles meeting at each corner
  const cornersOf = (g: number, field: (g: number) => number): void => {
    const nw = g - SPAN - 1, n = g - SPAN, ne = g - SPAN + 1, w = g - 1, e = g + 1, sw = g + SPAN - 1, s = g + SPAN, se = g + SPAN + 1;
    const c0 = field(g);
    v4[0] = (field(nw) + field(n) + field(w) + c0) / 4; v4[1] = (field(n) + field(ne) + c0 + field(e)) / 4;
    v4[2] = (c0 + field(e) + field(s) + field(se)) / 4; v4[3] = (field(w) + c0 + field(sw) + field(s)) / 4;
  };
  // a ±1 class field blurred over the 3×3 round each tile, then its corner means — one fixed 4×4 kernel per corner over
  // the 5×5 block round the tile (weights 1-2-2-1 each way, /36), the block's signs read once. `kind` 0: the class pair
  // (a → +1, b → −1, else 0); 1: the terrace threshold a (at or above → +1, below or water → −1)
  const blurredCorners = (g: number, kind: number, a: number, b: number): void => {
    for (let j = -2; j <= 2; j++) for (let k = -2; k <= 2; k++) {
      const q = g + j * SPAN + k;
      s25[(j + 2) * 5 + (k + 2)] = kind === 0 ? (tid[q] === a ? 1 : tid[q] === b ? -1 : 0) : water[q] ? -1 : level[q] >= a ? 1 : -1;
    }
    // corner (cx, cy) ∈ {0,1}²: the 4×4 block from (cx − 2, cy − 2), weights 1,2,2,1 along each axis
    for (let cy = 0; cy < 2; cy++) for (let cx = 0; cx < 2; cx++) {
      let acc = 0;
      for (let j = 0; j < 4; j++) { const wy = j === 0 || j === 3 ? 1 : 2, row = (j + cy) * 5 + cx; acc += wy * (s25[row] + 2 * s25[row + 1] + 2 * s25[row + 2] + s25[row + 3]); }
      v4[cy === 0 ? cx : 3 - cx] = acc / 36;
    }
  };
  // 2. the paint, a row at a time: the base colours as runs (a row of grass is one rect, not sixty-four), then each
  // tile's extras over them — a tile's polygons never leave its tile, so the order within a row is free
  for (let ly = 0; ly < CHUNK; ly++) {
  if (ly && ly % rows === 0) yield;
  { let run0 = 0, runC = colour[(ly + PAD) * SPAN + PAD];
    for (let lx = 1; lx <= CHUNK; lx++) { const c = lx < CHUNK ? colour[(ly + PAD) * SPAN + (lx + PAD)] : ''; if (c !== runC) { p.rect(run0, ly, lx - run0, 1, runC); run0 = lx; runC = c; } } }
  for (let lx = 0; lx < CHUNK; lx++) {
    const g = (ly + PAD) * SPAN + (lx + PAD), own = colour[g], w = water[g] === 1, ti = tid[g];
    let shoreSide = -1;
    for (let k = 0; k < 4; k++) { const og = g + SIDES[k][0] + SIDES[k][1] * SPAN; if (water[og] !== water[g]) { shoreSide = og; break; } }
    if (shoreSide >= 0) {
      // the shore: the land colour under, the wet part over it — the shallows, then the deep water
      const landG = w ? shoreSide : g, waterG = w ? g : shoreSide;
      const deep = colour[waterG], shallow = mix(deep, SHALLOW_TINT, SHALLOW_MIX);
      if (colour[landG] !== own) p.rect(lx, ly, 1, 1, colour[landG]); // (the base run painted the tile's own colour already)
      cornersOf(g, shore);
      const wet = belowPoly(v4, 0, lx, ly); if (wet.length >= 3) p.poly(wet, shallow);
      const dp = belowPoly(v4, SHALLOW_AT, lx, ly); if (dp.length >= 3) p.poly(dp, deep);
    } else {
      if (w) {
        // open water with land at a diagonal: the shallows' outer edge may still run through
        let landNear = false;
        for (let j = -1; j <= 1 && !landNear; j++) for (let k = -1; k <= 1; k++) if (!water[g + j * SPAN + k]) { landNear = true; break; }
        if (landNear) {
          cornersOf(g, shore);
          if (v4[0] > SHALLOW_AT || v4[1] > SHALLOW_AT || v4[2] > SHALLOW_AT || v4[3] > SHALLOW_AT) {
            p.rect(lx, ly, 1, 1, mix(own, SHALLOW_TINT, SHALLOW_MIX));
            const dp = belowPoly(v4, SHALLOW_AT, lx, ly); if (dp.length >= 3) p.poly(dp, own);
          }
        }
      }
    }
    if (w) continue;
    // land against land: for each neighbouring class, the contour of its blurred ±1 field, painted in the neighbour's colour
    let done0 = -1, done1 = -1;
    for (let k = 0; k < 4; k++) {
      const og = g + SIDES[k][0] + SIDES[k][1] * SPAN, ot = tid[og];
      if (ot === ti || water[og] || ot === done0 || ot === done1) continue;
      if (done0 < 0) done0 = ot; else done1 = ot;
      blurredCorners(g, 0, ti, ot);
      const poly = belowPoly(v4, 0, lx, ly); if (poly.length >= 3) p.poly(poly, colour[og]);
    }
    // the terrace steps: the contour of the terraced height, one line per level threshold met round the tile
    let lo = level[g], hi = lo;
    for (let j = -1; j <= 1; j++) for (let k = -1; k <= 1; k++) { const q = g + j * SPAN + k; if (water[q]) continue; const l = level[q]; if (l < lo) lo = l; else if (l > hi) hi = l; }
    if (hi > lo) {
      const t = list[ti], dark = groundColour(t.color, -40, c.height[ly * CHUNK + lx]);
      for (let thr = lo + 1; thr <= hi; thr++) {
        blurredCorners(g, 1, thr, 0);
        const cr = crossings(v4, 0, lx, ly);
        if (cr.length >= 2) p.line([cr[0], cr[1]], dark, 0.2);
        if (cr.length === 4) p.line([cr[2], cr[3]], dark, 0.2);
      }
    }
  }
  }
}
